import { effect, signal } from '@preact/signals';
import type { PlaybackState, TimingTrack } from '@fcbh/audio';
import { Player } from './player.js';
import { acquireWakeLock, releaseWakeLock } from './wake-lock.js';
import {
  hasApiKey,
  loadBooks,
  loadChapterAudio,
  loadChapterText,
  resolveUsxFileset,
} from './biblebrain.js';
import { isPublisherMode, publisher } from '../publisher/mode.js';
import { currentTrack } from '../karaoke/highlight.js';
import { route, playback } from '../state/store.js';
import { playerSettings } from '../state/player-settings.js';
import { readingPrefs } from '../state/reading-prefs.js';
import { sampleTimingFor } from '../data/sample-timing.js';
import { sampleChapterFor, SAMPLE_CHAPTERS } from '../data/sample-chapter.js';
import { clearResume, getResume, setResume } from '../state/resume-store.js';
import { consumeEndOfChapter, registerPauseFn } from '../state/sleep-timer.js';
import { currentBible } from '../state/bible-store.js';
import { markChapterHeard } from '../state/progress-store.js';
import { visitChapter } from '../state/history-store.js';
import { currentPlaylist, advancePlaylist } from '../state/playlist-store.js';
import {
  appendedChapters,
  books,
  chapterError,
  chapterLoading,
  currentChapter,
  type ReaderChapter,
} from '../state/chapter-store.js';
import { getOfflineManifest, offlineKey } from '../storage/offline-manifest.js';
import { offlineGet } from '../storage/offline-storage.js';

export type ChapterStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'demo' | 'error';

export const chapterStatus = signal<ChapterStatus>('idle');

/** Last saved resume position for the current chapter (seconds). 0 if none. */
export const resumeAvailable = signal<number>(0);

const RESUME_SAVE_INTERVAL_MS = 4000;
const CHAPTER_END_PAUSE_MS = 1000;

let player: Player | null = null;

export function getPlayer(): Player {
  if (!player) player = new Player();
  return player;
}

const synth = {
  playing: false,
  currentSec: 0,
  durationSec: 0,
  lastTickMs: 0,
  raf: 0 as number,
};

function syncSynthState(): void {
  const next: PlaybackState = {
    playing: synth.playing,
    currentSec: synth.currentSec,
    durationSec: synth.durationSec,
    rate: playerSettings.value.rate,
    bufferedSec: synth.durationSec,
  };
  playback.value = next;
}

function tickSynth(now: number): void {
  if (!synth.playing) return;
  const dt = (now - synth.lastTickMs) / 1000;
  synth.lastTickMs = now;
  synth.currentSec = Math.min(synth.durationSec, synth.currentSec + dt * playerSettings.value.rate);
  syncSynthState();
  if (synth.currentSec >= synth.durationSec) {
    synth.playing = false;
    releaseWakeLock();
    syncSynthState();
    onChapterEnd();
    return;
  }
  synth.raf = requestAnimationFrame(tickSynth);
}

function startSynth(): void {
  if (synth.playing) return;
  if (synth.currentSec >= synth.durationSec) synth.currentSec = 0;
  synth.playing = true;
  synth.lastTickMs = performance.now();
  synth.raf = requestAnimationFrame(tickSynth);
  // Karaoke-demo playback runs off a rAF ticker, not the <audio> element, so
  // the Player's sync handler never fires — drive the wake lock directly here.
  acquireWakeLock();
  syncSynthState();
}

function pauseSynth(): void {
  if (!synth.playing) return;
  synth.playing = false;
  if (synth.raf) cancelAnimationFrame(synth.raf);
  releaseWakeLock();
  syncSynthState();
}

function loadSynth(timing: TimingTrack | null): void {
  pauseSynth();
  synth.currentSec = 0;
  synth.durationSec = timing && timing.verses.length > 0
    ? Math.max(...timing.verses.map((v) => v.endSec))
    : 0;
  syncSynthState();
}

function isDemoMode(): boolean {
  return chapterStatus.value === 'demo';
}

/**
 * Wrap the chapter-load effect's body so it can be re-invoked by the
 * retry button without depending on the signal graph noticing.
 */
let loadToken = 0;
let lastLoadParams: { book: string; chapter: number } | null = null;

/** Prefetched data for the next chapter, ready for an instant buffer swap. */
type PrefetchResult = {
  book: string;
  chapter: number;
  audioUrl: string;
  timing: TimingTrack | null;
  verses: { ref: string; number: number; text: string }[];
};

let prefetchToken = 0;
let prefetchResult: PrefetchResult | null = null;
/**
 * Set after a successful swapBuffer() so the route-change effect's
 * loadCurrentChapter() call knows to skip the API round-trip.
 */
let swapApplied: { book: string; chapter: number } | null = null;

async function loadCurrentChapter(opts: { resumePlaying?: boolean } = {}): Promise<void> {
  const { book, chapter } = route.value;

  // Fast path: swapBuffer() already applied this chapter — skip the API round-trip.
  if (swapApplied && swapApplied.book === book && swapApplied.chapter === chapter) {
    swapApplied = null;
    lastLoadParams = { book, chapter };
    appendedChapters.value = [];
    resumeAvailable.value = 0;
    const nn = nextChapter(book, chapter);
    if (nn) void startPrefetch(nn.book, nn.chapter);
    return;
  }

  // Offline intercept — serve from IDB if this book has been downloaded.
  const { bibleId: offlineBibleId } = currentBible.peek();
  const offlineManifest = await getOfflineManifest(offlineBibleId);
  if (offlineManifest?.completedBooks.includes(book.toUpperCase())) {
    const [audioBuffer, textBuffer] = await Promise.all([
      offlineGet('chunks', offlineKey(offlineBibleId, book, chapter, 'audio')),
      offlineGet('chapters', offlineKey(offlineBibleId, book, chapter, 'text')),
    ]);
    if (audioBuffer && textBuffer) {
      const audioBlob = new Blob([audioBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const verses = JSON.parse(new TextDecoder().decode(textBuffer)) as { ref: string; number: number; text: string }[];
      const p = getPlayer();
      p.setSource(audioUrl);
      currentChapter.value = composeChapter(book, chapter, verses);
      visitChapter(book, chapter, offlineBibleId);
      currentTrack.value = null;
      chapterStatus.value = 'ready';
      chapterLoading.value = false;
      chapterError.value = null;
      appendedChapters.value = [];
      lastLoadParams = { book, chapter };
      p.setRate(playerSettings.value.rate);
      setMediaMetadata(book, chapter);
      return;
    }
  }

  // Cancel any stale prefetch when the user navigates to a different chapter.
  clearPrefetch();

  const audioStyle = playerSettings.value.audioStyle;
  const { bibleId } = currentBible.value;
  const token = ++loadToken;
  lastLoadParams = { book, chapter };
  pauseSynth();
  // Reset continuous-scroll tail whenever the focused chapter changes.
  appendedChapters.value = [];
  chapterLoading.value = true;
  chapterError.value = null;
  chapterStatus.value = 'loading';

  const wasPlaying = opts.resumePlaying ?? playback.peek().playing;
  const p = getPlayer();
  if (wasPlaying) p.pause();

  try {
    const [audio, text] = await Promise.all([
      loadChapterAudio(book, chapter, audioStyle, bibleId),
      loadChapterText(book, chapter, bibleId),
    ]);
    if (token !== loadToken) return;

    const timing: TimingTrack | null = audio.timing ?? sampleTimingFor(book, chapter, audioStyle);
    currentTrack.value = timing;

    // In publisher mode, an empty verses array is the truth (audio-only
    // bible). Falling back to ESV samples would inject English text over a
    // non-English audio bake — exactly the "English text overlay on Hausa
    // audio" bug that surfaced on the first HAUCLV publish.
    const inPublisherMode = isPublisherMode();
    let verses =
      text.verses.length > 0
        ? text.verses
        : inPublisherMode
          ? []
          : sampleVersesFor(book, chapter);
    if (verses.length === 0 && !audio.audioUrl) {
      chapterStatus.value = hasApiKey() ? 'error' : 'demo';
      if (chapterStatus.value === 'error') {
        chapterError.value = `No content available for ${book} ${chapter}.`;
      }
    }

    // Overlay USX paragraph data when paragraphMode is on.
    if (readingPrefs.peek().paragraphMode && verses.length > 0) {
      const usxFilesetId = resolveUsxFileset(book, bibleId);
      const apiKeyVal = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BIBLE_BRAIN_KEY ?? '';
      try {
        const { loadChapterUsxMap } = await import('./usx-parser.js');
        const usxMap = await loadChapterUsxMap(book, chapter, usxFilesetId, apiKeyVal);
        if (usxMap !== null) {
          verses = verses.map((v) => {
            const entry = usxMap.get(v.number);
            if (!entry) return v;
            return {
              ...v,
              paraStyle: entry.style,
              isFirstInPara: entry.isFirstInPara,
              headingBefore: entry.headingBefore,
            };
          });
        }
      } catch {
        // USX fetch failed — graceful degradation, leave verses as-is
      }
    }

    currentChapter.value = composeChapter(book, chapter, verses);
    visitChapter(book, chapter, bibleId);
    // Cross-refs are only used inside <VerseMenu> (tap-to-open). They
    // lazy-load on demand via refsForVerse() — no need to pay for the
    // round-trip on every chapter change when most users never tap a
    // verse menu in a session.

    const resumeSec = getResume(book, chapter, bibleId);
    resumeAvailable.value = wasPlaying ? 0 : resumeSec;
    if (audio.audioUrl) {
      p.setSource(audio.audioUrl);
      chapterStatus.value = 'ready';
      p.setRate(playerSettings.value.rate);
      if (resumeSec > 0 && wasPlaying) p.seekOnLoad(resumeSec);
      setMediaMetadata(book, chapter);
      if (wasPlaying) await p.play().catch(() => undefined);
      // Kick off background prefetch for the next chapter so chapter-end
      // transitions can swap without waiting for an API round-trip.
      const nextRef = nextChapter(book, chapter);
      if (nextRef) void startPrefetch(nextRef.book, nextRef.chapter);
    } else if (timing) {
      p.setSource('');
      loadSynth(timing);
      if (resumeSec > 0 && resumeSec < synth.durationSec && wasPlaying) {
        synth.currentSec = resumeSec;
        syncSynthState();
      }
      chapterStatus.value = 'demo';
      if (wasPlaying) startSynth();
    } else {
      p.setSource('');
      chapterStatus.value = hasApiKey() ? 'error' : 'unavailable';
      if (chapterStatus.value === 'error') {
        chapterError.value = `No audio or timing for ${book} ${chapter}.`;
      }
    }
  } catch (err) {
    if (token !== loadToken) return;
    chapterStatus.value = 'error';
    chapterError.value = (err instanceof Error ? err.message : 'Failed to load chapter.');
  } finally {
    if (token === loadToken) chapterLoading.value = false;
  }
}

export function retryLoad(): void {
  void loadCurrentChapter({ resumePlaying: false });
}

function clearPrefetch(): void {
  prefetchToken++;
  prefetchResult = null;
}

async function startPrefetch(book: string, chapter: number): Promise<void> {
  clearPrefetch();
  const token = prefetchToken;
  const audioStyle = playerSettings.peek().audioStyle;
  const { bibleId } = currentBible.peek();
  try {
    const [audio, text] = await Promise.all([
      loadChapterAudio(book, chapter, audioStyle, bibleId),
      loadChapterText(book, chapter, bibleId),
    ]);
    if (token !== prefetchToken) return;
    if (!audio.audioUrl) return;
    const timing = audio.timing ?? sampleTimingFor(book, chapter, audioStyle);
    const verses = text.verses.length > 0
      ? text.verses
      : isPublisherMode()
        ? []
        : sampleVersesFor(book, chapter);
    prefetchResult = { book, chapter, audioUrl: audio.audioUrl, timing, verses };
    getPlayer().prefetch(audio.audioUrl);
  } catch {
    // Prefetch failure is silent — chapter-end handler falls back to the slow path.
  }
}

export function pausePlayback(): void {
  if (isDemoMode()) {
    pauseSynth();
    return;
  }
  getPlayer().pause();
}

export function initPlayerController(): void {
  const p = getPlayer();

  registerPauseFn(pausePlayback);

  effect(() => {
    p.setRate(playerSettings.value.rate);
  });

  // React to route, audioStyle, bible, or paragraphMode changes — reload chapter.
  effect(() => {
    void route.value;
    void playerSettings.value.audioStyle;
    void currentBible.value;
    void readingPrefs.value.paragraphMode;
    void loadCurrentChapter();
  });

  // When the active bible changes, refresh the book list.
  effect(() => {
    const { bibleId } = currentBible.value;
    void loadBooks(bibleId).then((b) => {
      if (b.length > 0) books.value = b;
      else books.value = sampleBookFallback();
    });
  });

  // Periodically persist position so resume survives reloads + tab close.
  let lastSavedSec = -1;
  let lastSavedMs = 0;
  effect(() => {
    const sec = playback.value.currentSec;
    if (!playback.value.playing) return;
    const now = Date.now();
    if (now - lastSavedMs < RESUME_SAVE_INTERVAL_MS) return;
    if (Math.abs(sec - lastSavedSec) < 1) return;
    lastSavedMs = now;
    lastSavedSec = sec;
    if (!lastLoadParams) return;
    setResume(lastLoadParams.book, lastLoadParams.chapter, sec, currentBible.peek().bibleId);
  });

  // Detect chapter-end for real audio (synth handles its own end).
  // Sticky listener — survives element swaps after swapBuffer().
  p.onEnded(() => {
    onChapterEnd();
  });

  // CloudFront pre-signed URLs expire (we've seen ~14d). When the browser
  // throws a network/decode error on a chapter we already had audio for,
  // re-fetch the chapter-media response to get a fresh signed URL and
  // resume from the same position.
  // Sticky listener — survives element swaps after swapBuffer().
  p.onError(() => {
    if (chapterStatus.value !== 'ready') return;
    const activeEl = getPlayer().element;
    const positionSec = activeEl.currentTime;
    void loadCurrentChapter({ resumePlaying: !activeEl.paused }).then(() => {
      if (positionSec > 0) getPlayer().seekOnLoad(positionSec);
    });
  });

  void loadCurrentChapter();
}

function sampleBookFallback(): import('./biblebrain.js').BookMeta[] {
  const grouped = new Map<string, number[]>();
  for (const c of SAMPLE_CHAPTERS) {
    const list = grouped.get(c.bookId) ?? [];
    if (!list.includes(c.chapter)) list.push(c.chapter);
    grouped.set(c.bookId, list);
  }
  const out: import('./biblebrain.js').BookMeta[] = [];
  for (const [bookId, chapters] of grouped.entries()) {
    chapters.sort((a, b) => a - b);
    out.push({
      bookId,
      bookName: BOOK_NAMES[bookId] ?? bookId,
      testament: 'NT',
      chapters,
    });
  }
  return out;
}

function onChapterEnd(): void {
  if (!lastLoadParams) return;
  // Reaching the end counts toward the user's reading progress regardless
  // of whether the sleep timer fires next.
  markChapterHeard(
    currentBible.peek().bibleId,
    lastLoadParams.book,
    lastLoadParams.chapter,
  );
  // Sleep timer's "end-of-chapter" mode takes precedence — stop here.
  if (consumeEndOfChapter()) return;
  // Active playlist takes precedence over sequential chapter walk.
  if (currentPlaylist.peek()) {
    const advanced = advancePlaylist();
    if (advanced) {
      const state = currentPlaylist.peek();
      const item = state?.playlist.items[state.index];
      if (item) {
        setTimeout(() => {
          route.value = { book: item.book, chapter: item.chapter };
          queueAutoplayOnNextReady();
        }, CHAPTER_END_PAUSE_MS);
      }
      return;
    }
  }
  const next = nextChapter(lastLoadParams.book, lastLoadParams.chapter);
  if (!next) return;
  setTimeout(() => {
    const p = getPlayer();
    const pr = prefetchResult;
    const swapped =
      pr !== null &&
      pr.book === next.book &&
      pr.chapter === next.chapter &&
      p.swapBuffer();

    if (swapped && pr !== null) {
      // Fast path — prefetch buffer is ready: instant hand-off, no HTTP round-trip.
      swapApplied       = { book: next.book, chapter: next.chapter };
      lastLoadParams    = { book: next.book, chapter: next.chapter };
      const { bibleId } = currentBible.peek();
      const verses = pr.verses.length > 0 ? pr.verses : sampleVersesFor(next.book, next.chapter);
      currentChapter.value = composeChapter(next.book, next.chapter, verses);
      currentTrack.value   = pr.timing;
      chapterStatus.value  = 'ready';
      visitChapter(next.book, next.chapter, bibleId);
      setMediaMetadata(next.book, next.chapter);
      route.value = { book: next.book, chapter: next.chapter };
      void p.play().catch(() => undefined);
    } else {
      // Slow path — buffer wasn't ready; load normally and autoplay when ready.
      route.value = next;
      queueAutoplayOnNextReady();
    }
  }, CHAPTER_END_PAUSE_MS);
}

let autoplayPending = false;

function queueAutoplayOnNextReady(): void {
  autoplayPending = true;
  const stop = effect(() => {
    const status = chapterStatus.value;
    if (!autoplayPending) {
      stop();
      return;
    }
    if (status === 'ready') {
      autoplayPending = false;
      stop();
      void getPlayer().play().catch(() => undefined);
    } else if (status === 'demo') {
      autoplayPending = false;
      stop();
      startSynth();
    } else if (status === 'error' || status === 'unavailable') {
      autoplayPending = false;
      stop();
    }
  });
}

function nextChapter(book: string, chapter: number): { book: string; chapter: number } | null {
  const list = books.value;
  if (list.length === 0) return null;
  const idx = list.findIndex((b) => b.bookId === book.toUpperCase());
  if (idx === -1) return null;
  const current = list[idx];
  if (!current) return null;
  // Find the smallest chapter strictly greater than the current one.
  const nextInBook = current.chapters
    .filter((c) => c > chapter)
    .sort((a, b) => a - b)[0];
  if (typeof nextInBook === 'number') {
    return { book: current.bookId, chapter: nextInBook };
  }
  const nextBook = list[idx + 1];
  if (!nextBook || nextBook.chapters.length === 0) return null;
  const first = [...nextBook.chapters].sort((a, b) => a - b)[0];
  if (typeof first !== 'number') return null;
  return { book: nextBook.bookId, chapter: first };
}

function prevChapter(book: string, chapter: number): { book: string; chapter: number } | null {
  const list = books.value;
  if (list.length === 0) return null;
  const idx = list.findIndex((b) => b.bookId === book.toUpperCase());
  if (idx === -1) return null;
  const current = list[idx];
  if (!current) return null;
  // Find the largest chapter strictly less than the current one.
  const prevInBook = current.chapters
    .filter((c) => c < chapter)
    .sort((a, b) => b - a)[0];
  if (typeof prevInBook === 'number') {
    return { book: current.bookId, chapter: prevInBook };
  }
  const prevBook = list[idx - 1];
  if (!prevBook || prevBook.chapters.length === 0) return null;
  const last = [...prevBook.chapters].sort((a, b) => b - a)[0];
  if (typeof last !== 'number') return null;
  return { book: prevBook.bookId, chapter: last };
}

/**
 * Jump to the next chapter in canon order. Returns true on navigation.
 * Used by the player bar's skip-next button.
 */
export function gotoNextChapter(): boolean {
  const list = books.value;
  if (list.length === 0) return false;
  const { book, chapter } = route.value;
  const next = nextChapter(book, chapter);
  if (!next) return false;
  route.value = next;
  return true;
}

/**
 * Jump to the previous chapter in canon order. Returns true on navigation.
 * Used by the player bar's skip-prev button.
 */
export function gotoPrevChapter(): boolean {
  const list = books.value;
  if (list.length === 0) return false;
  const { book, chapter } = route.value;
  const prev = prevChapter(book, chapter);
  if (!prev) return false;
  route.value = prev;
  return true;
}

function currentVerseIndex(): number {
  const track = currentTrack.peek();
  if (!track || track.verses.length === 0) return -1;
  const t = playback.peek().currentSec;
  let idx = -1;
  for (let i = 0; i < track.verses.length; i++) {
    const cue = track.verses[i];
    if (cue && cue.beginSec <= t + 0.001) idx = i;
    else break;
  }
  return idx;
}

/**
 * Seek to the next verse cue in the current chapter. No-op at the last
 * verse — chapter cross-over belongs to the skip buttons. Used by the
 * scrub-fwd button.
 */
export function gotoNextVerse(): boolean {
  const track = currentTrack.peek();
  if (!track || track.verses.length === 0) return false;
  const idx = currentVerseIndex();
  const next = track.verses[idx + 1];
  if (!next) return false;
  seekAbsolute(next.beginSec);
  return true;
}

/**
 * Seek to the start of the previous verse. If we're more than 1.2s into
 * the current verse, snap to its start instead — matches the podcast-app
 * convention where a back-tap rewinds to the top of the current item
 * unless you're right at the top. Used by the scrub-bck button.
 */
export function gotoPrevVerse(): boolean {
  const track = currentTrack.peek();
  if (!track || track.verses.length === 0) return false;
  const idx = currentVerseIndex();
  if (idx < 0) return false;
  const here = track.verses[idx];
  if (!here) return false;
  const t = playback.peek().currentSec;
  if (t - here.beginSec > 1.2) {
    seekAbsolute(here.beginSec);
    return true;
  }
  const prev = track.verses[idx - 1];
  if (!prev) {
    seekAbsolute(here.beginSec);
    return true;
  }
  seekAbsolute(prev.beginSec);
  return true;
}

function composeChapter(
  book: string,
  chapter: number,
  verses: { ref: string; number: number; text: string }[],
): ReaderChapter {
  const bookId = book.toUpperCase();
  // Publisher mode — use the baked manifest's book name + language so the
  // chapter title reads "MATIYU 1" / "ha-NG" rather than the ESV defaults.
  // The audio-only view in particular exposes any English fallback because
  // the verse list isn't there to mask it.
  const pub = publisher.value;
  if (pub) {
    const bookEntry = pub.books.find((b) => b.bookId === bookId);
    return {
      bookId,
      bookName: bookEntry?.bookName ?? BOOK_NAMES[bookId] ?? book,
      chapter,
      language: { iso639_3: pub.languageIso, bcp47: pub.bcp47 },
      source: { translation: pub.displayName, license: 'Bible Brain' },
      verses,
    };
  }
  return {
    bookId,
    bookName: BOOK_NAMES[bookId] ?? book,
    chapter,
    language: { iso639_3: 'eng', bcp47: 'en' },
    source: { translation: 'ESV', license: 'Crossway' },
    verses,
  };
}

function sampleVersesFor(book: string, chapter: number): { ref: string; number: number; text: string }[] {
  const c = sampleChapterFor(book, chapter);
  if (!c) return [];
  return c.verses.map((v) => ({ ref: v.ref, number: v.number, text: v.text }));
}

export async function togglePlay(): Promise<void> {
  // Acting on the player dismisses the resume hint — the user has chosen
  // whether to honor it or not.
  resumeAvailable.value = 0;
  if (isDemoMode()) {
    if (synth.playing) pauseSynth();
    else startSynth();
    return;
  }
  const p = getPlayer();
  if (p.isPlaying()) p.pause();
  else await p.play().catch(() => undefined);
}

export async function resumeFromSaved(): Promise<void> {
  const sec = resumeAvailable.peek();
  if (sec <= 0) return;
  seekAbsolute(sec);
  resumeAvailable.value = 0;
  if (isDemoMode()) {
    if (!synth.playing) startSynth();
    return;
  }
  const p = getPlayer();
  if (!p.isPlaying()) await p.play().catch(() => undefined);
}

export function dismissResume(): void {
  if (!lastLoadParams) return;
  clearResume(lastLoadParams.book, lastLoadParams.chapter, currentBible.peek().bibleId);
  resumeAvailable.value = 0;
}

export function seekRelative(deltaSec: number): void {
  if (isDemoMode()) {
    synth.currentSec = Math.max(0, Math.min(synth.durationSec, synth.currentSec + deltaSec));
    syncSynthState();
    return;
  }
  getPlayer().seekBy(deltaSec);
}

export function seekAbsolute(sec: number): void {
  if (isDemoMode()) {
    synth.currentSec = Math.max(0, Math.min(synth.durationSec, sec));
    syncSynthState();
    return;
  }
  getPlayer().seek(sec);
}

let appending = false;

/**
 * Append the next chapter (after the last currently visible chapter)
 * to `appendedChapters` for continuous-scroll reading. Audio + karaoke
 * stay bound to `currentChapter`; this only extends the text below.
 *
 * Returns true if a chapter was appended; false if at the end of canon
 * or if a load is already in flight.
 */
export async function appendNextChapter(): Promise<boolean> {
  if (appending) return false;
  const head = currentChapter.peek();
  if (!head) return false;
  const tail = appendedChapters.peek();
  const last = tail[tail.length - 1] ?? head;
  const nextRef = nextChapter(last.bookId, last.chapter);
  if (!nextRef) return false;

  appending = true;
  try {
    const text = await loadChapterText(nextRef.book, nextRef.chapter, currentBible.peek().bibleId);
    const verses = text.verses.length > 0
      ? text.verses
      : sampleVersesFor(nextRef.book, nextRef.chapter);
    if (verses.length === 0) return false;
    const appended = composeChapter(nextRef.book, nextRef.chapter, verses);
    appendedChapters.value = [...appendedChapters.peek(), appended];
    return true;
  } catch {
    return false;
  } finally {
    appending = false;
  }
}

export async function playFromVerse(verseRef: string): Promise<void> {
  const cue = currentTrack.value?.verses.find((v) => v.verseRef === verseRef);
  if (!cue) return;
  seekAbsolute(cue.beginSec);
  if (isDemoMode()) {
    if (!synth.playing) startSynth();
    return;
  }
  const p = getPlayer();
  if (!p.isPlaying()) await p.play().catch(() => undefined);
}

function setMediaMetadata(book: string, chapter: number): void {
  if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
  const bookName = BOOK_NAMES[book.toUpperCase()] ?? book;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: `${bookName} ${chapter}`,
    artist: 'Bible Brain · ESV',
    album: 'Geauxjüre Reader',
    artwork: [
      { src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png' },
      { src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' },
    ],
  });
}

const BOOK_NAMES: Record<string, string> = {
  GEN: 'Genesis', EXO: 'Exodus', LEV: 'Leviticus', NUM: 'Numbers', DEU: 'Deuteronomy',
  JOS: 'Joshua', JDG: 'Judges', RUT: 'Ruth', '1SA': '1 Samuel', '2SA': '2 Samuel',
  '1KI': '1 Kings', '2KI': '2 Kings', '1CH': '1 Chronicles', '2CH': '2 Chronicles',
  EZR: 'Ezra', NEH: 'Nehemiah', EST: 'Esther', JOB: 'Job', PSA: 'Psalms', PRO: 'Proverbs',
  ECC: 'Ecclesiastes', SNG: 'Song of Solomon', ISA: 'Isaiah', JER: 'Jeremiah',
  LAM: 'Lamentations', EZK: 'Ezekiel', DAN: 'Daniel', HOS: 'Hosea', JOL: 'Joel',
  AMO: 'Amos', OBA: 'Obadiah', JON: 'Jonah', MIC: 'Micah', NAM: 'Nahum', HAB: 'Habakkuk',
  ZEP: 'Zephaniah', HAG: 'Haggai', ZEC: 'Zechariah', MAL: 'Malachi',
  MAT: 'Matthew', MRK: 'Mark', LUK: 'Luke', JHN: 'John', ACT: 'Acts',
  ROM: 'Romans', '1CO': '1 Corinthians', '2CO': '2 Corinthians', GAL: 'Galatians',
  EPH: 'Ephesians', PHP: 'Philippians', COL: 'Colossians', '1TH': '1 Thessalonians',
  '2TH': '2 Thessalonians', '1TI': '1 Timothy', '2TI': '2 Timothy', TIT: 'Titus',
  PHM: 'Philemon', HEB: 'Hebrews', JAS: 'James', '1PE': '1 Peter', '2PE': '2 Peter',
  '1JN': '1 John', '2JN': '2 John', '3JN': '3 John', JUD: 'Jude', REV: 'Revelation',
};
